import { useEffect, useRef } from 'react';
import { Reveal, SectionHead } from './UI';
import SplitText from './SplitText';
import SpotlightCard from './SpotlightCard';

const CAPS = [
  { title: 'PPE Violations', body: 'Flags missing helmets, vests and other required gear in real time, before an incident can occur.', image: '/PPE%20Violation.webp' },
  { title: 'Idle Machinery', body: 'Detects stalled lines and equipment sitting unused mid-shift so supervisors can act fast.', image: '/Idle%20Machinery.webp' },
  { title: 'Unauthorised Access', body: 'Spots entry into restricted zones — stores, control rooms, pump rooms — the moment it happens.', image: '/unauthorised%20access.webp' },
  { title: 'Loitering & Inactivity', body: 'Flags prolonged inactivity, phone use or unauthorised idling in sensitive areas on shift.', image: '/loitering%20and%20inactivity.webp' },
  { title: 'Attendance & Presence', body: 'Logs entry, exit and zone-level presence without manual sign-in or badge scanning.', image: '/attendance%20and%20presence.webp' },
];

function MarqueeRow({ items, speed = 0.15 }) {
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const drag = useRef({ on: false, startX: 0, startPos: 0 });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    requestAnimationFrame(() => {
      const setW = track.scrollWidth / 3;

      function tick() {
        if (!drag.current.on) {
          posRef.current -= speed;
          if (posRef.current <= -setW) posRef.current += setW;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
        rafRef.current = requestAnimationFrame(tick);
      }
      rafRef.current = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(rafRef.current);
  }, [speed]);

  const grab = (x) => { drag.current = { on: true, startX: x, startPos: posRef.current }; };
  const move = (x) => { if (drag.current.on) posRef.current = drag.current.startPos + (x - drag.current.startX); };
  const release = () => { drag.current.on = false; };

  const tripled = [...items, ...items, ...items];

  return (
    <div
      className="mq-clip"
      onMouseDown={e => grab(e.clientX)}
      onMouseMove={e => move(e.clientX)}
      onMouseUp={release}
      onMouseLeave={release}
      onTouchStart={e => grab(e.touches[0].clientX)}
      onTouchMove={e => { e.preventDefault(); move(e.touches[0].clientX); }}
      onTouchEnd={release}
    >
      <div ref={trackRef} className="mq-track">
        {tripled.map((c, i) => (
          <SpotlightCard
            key={i}
            className="mq-card"
            spotlightColor="rgba(124, 58, 237, 0.28)"
          >
            {c.image && (
              <div className="mq-card-img">
                <img
                  src={c.image}
                  alt={c.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            )}
            <SplitText
              tag="h4"
              text={c.title}
              delay={30}
              duration={0.8}
              ease="power3.out"
              splitType="words"
              from={{ opacity: 0, y: 15 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-40px"
              textAlign="left"
              className="mq-card-title"
            />
            <SplitText
              tag="p"
              text={c.body}
              delay={10}
              duration={0.7}
              ease="power2.out"
              splitType="words"
              from={{ opacity: 0, y: 10 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-40px"
              textAlign="left"
              className="mq-card-body"
            />
          </SpotlightCard>
        ))}
      </div>
    </div>
  );
}

export default function Capabilities() {
  return (
    <section id="capabilities" className="section section-cream">
      <style>{`
        #capabilities { padding-block: clamp(28px, 3vw, 40px) !important; }
        #capabilities [class*="eyebrow"]::before,
        #capabilities [class*="eyebrow"]::after { content:none!important; display:none!important; }

        #capabilities .cap-outer {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(16px, 3vw, 24px);
        }

        /* Marquee */
        #capabilities .mq-clip {
          overflow: hidden;
          cursor: grab;
          user-select: none;
          -webkit-user-select: none;
          touch-action: pan-y;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
        }

        #capabilities .mq-track {
          display: flex;
          gap: 16px;
          width: max-content;
          will-change: transform;
          padding-block: 10px;
        }

        /* Card — SpotlightCard base + our sizing/colors, spotlight is purple only */
        #capabilities .mq-card {
          width: 300px;
          flex-shrink: 0;
          background: #fff;
          border: 1.5px solid rgba(124, 58, 237, 0.35);
          border-radius: 12px;
          padding: 22px 20px 24px;
          pointer-events: auto;
        }

        #capabilities .mq-card-img {
          width: 100%;
          height: 180px;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 14px;
          border: 1px solid rgba(124, 58, 237, 0.18);
        }

        #capabilities .mq-card-title {
          font-family: var(--font-display, serif);
          font-size: 17px;
          font-weight: 500;
          color: #6D28D9;
          margin: 0 0 10px;
          line-height: 1.25;
        }

        #capabilities .mq-card-body {
          font-size: 13.5px;
          color: var(--ink-soft, #6B6760);
          line-height: 1.65;
          margin: 0;
        }

        #capabilities .cap-note {
          margin-top: 24px;
          font-size: 12.5px;
          color: var(--ink-faint, #BBB);
          font-family: var(--font-mono, monospace);
          text-align: center;
        }
      `}</style>

      <div className="cap-outer">

        <Reveal>
          <SectionHead
            center
            eyebrow="What TruVue AI Catches"
            h2="Built for the events that actually cost you"
            p="Each detection type is configured to your site, zones and shift patterns — not a generic off-the-shelf model."
          />
        </Reveal>

        {/* Slider only — slow marquee speed */}
        <MarqueeRow items={CAPS} speed={0.15} />

        <Reveal delay={100}>
          <SplitText
            tag="p"
            text="* Capability list is configured to your site and zones."
            delay={10}
            duration={0.7}
            ease="power2.out"
            splitType="words"
            from={{ opacity: 0, y: 10 }}
            to={{ opacity: 1, y: 0 }}
            threshold={0.1}
            rootMargin="-40px"
            textAlign="center"
            className="cap-note"
            style={{ textAlign: 'center', marginTop: 24 }}
          />
        </Reveal>

      </div>
    </section>
  );
}